import { useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import type {
  ParsedJsonUpload,
  PipelineStepId,
  PipelineStepState,
} from '../types';
import { parseJsonFileContent } from '../utils';
import { createInitialStepState } from './pipeline-step-state.utils';

type UsePipelineUploadsOptions = {
  organizationId: string;
  assetId: string;
  setUploads: Dispatch<SetStateAction<ParsedJsonUpload[]>>;
  setSteps: Dispatch<SetStateAction<Record<PipelineStepId, PipelineStepState>>>;
  resetPipeline: () => void;
  updateStepInput: (
    stepId: PipelineStepId,
    input: unknown,
    notify?: boolean,
  ) => void;
};

export function usePipelineUploads({
  organizationId,
  assetId,
  setUploads,
  setSteps,
  resetPipeline,
  updateStepInput,
}: UsePipelineUploadsOptions) {
  const { t } = useTranslation('pipelineDebug');

  const requireContext = useCallback(() => {
    if (!organizationId) {
      message.error(t('messages.organizationRequired'));
      return false;
    }

    if (!assetId) {
      message.error(t('messages.assetRequired'));
      return false;
    }

    return true;
  }, [organizationId, assetId, t]);

  const ingestFiles = useCallback(
    async (files: File[]) => {
      if (!requireContext()) {
        return;
      }

      const parsedUploads: ParsedJsonUpload[] = [];
      const errors: string[] = [];

      for (const file of files) {
        const content = await file.text();
        const parsed = parseJsonFileContent(file.name, content);

        if ('error' in parsed) {
          errors.push(parsed.error);
          continue;
        }

        parsedUploads.push({
          id: crypto.randomUUID(),
          fileName: file.name,
          records: parsed.records,
        });
      }

      if (errors.length > 0) {
        message.error(errors.join('\n'));
      }

      if (parsedUploads.length === 0) {
        return;
      }

      setUploads((current) => [...current, ...parsedUploads]);
      resetPipeline();
      setSteps((current) => ({
        ...current,
        lin: {
          ...createInitialStepState(),
          status: 'ready',
          input: parsedUploads.flatMap((upload) => upload.records),
        },
      }));
      message.success(
        t('messages.filesQueued', { count: parsedUploads.length }),
      );
    },
    [requireContext, resetPipeline, setSteps, setUploads, t],
  );

  const removeUpload = useCallback(
    (uploadId: string) => {
      setUploads((current) => {
        const nextUploads = current.filter((upload) => upload.id !== uploadId);
        setSteps((stepState) => ({
          ...stepState,
          lin: {
            ...createInitialStepState(),
            status: nextUploads.length > 0 ? 'ready' : 'idle',
            input: nextUploads.flatMap((upload) => upload.records),
          },
        }));
        resetPipeline();
        return nextUploads;
      });
    },
    [resetPipeline, setSteps, setUploads],
  );

  const ingestStepInputFiles = useCallback(
    async (stepId: PipelineStepId, files: File[]) => {
      if (!requireContext()) {
        return;
      }

      const parsedRecords: unknown[] = [];
      const errors: string[] = [];

      for (const file of files) {
        const content = await file.text();
        const parsed = parseJsonFileContent(file.name, content);

        if ('error' in parsed) {
          errors.push(parsed.error);
          continue;
        }

        parsedRecords.push(...parsed.records);
      }

      if (errors.length > 0) {
        message.error(errors.join('\n'));
      }

      if (parsedRecords.length === 0) {
        return;
      }

      const input =
        stepId === 'lin'
          ? parsedRecords
          : parsedRecords.length === 1
            ? parsedRecords[0]
            : parsedRecords;

      updateStepInput(stepId, input, false);
      message.success(t('messages.stepInputUploaded', { count: files.length }));
    },
    [requireContext, t, updateStepInput],
  );

  return {
    ingestFiles,
    removeUpload,
    ingestStepInputFiles,
  };
}
